import {BackendApi} from "../../../../../api/api.ts";
import {useEffect, useState} from "react";
import {DocumentDropzone} from "../../../entrepreneur/pages/ShareDocumentsPage/DocumentDropzone";
import {SharedDocument} from "../../../entrepreneur/pages/ShareDocumentsPage/SharedDocument.ts";
import {SharedDocumentsTable} from "../../../entrepreneur/pages/ShareDocumentsPage/SharedDocumentsTable.tsx";
import {useNotifications} from "../../../../../lib/NotificationContext.tsx";
import LoadingFrame from "../../../../../components/LoadingFrame.tsx";
import {Business} from "./BusinessesOverviewPage.tsx";
import {Field, Label, Switch} from "@headlessui/react";

const maxFiles = 60;
const maxSize = 1024 * 1024 * 500;

const supportedFileTypes = {
    'image/jpeg': ['.jpeg', '.jpg'],
    'image/png': ['.png'],
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/msword': ['.doc'],
    'application/vnd.oasis.opendocument.text': ['.odt'],
    'application/xml': ['.xml'],
};

const supportedFileExtensions = Object.values(supportedFileTypes).flat();

export function BusinessDocuments({business, api}: { business: Business, api: BackendApi }) {
    const [fileUploadHistory, setFileUploadHistory] = useState<SharedDocument[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const {dispatch} = useNotifications();
    const [forwardToPartner, setForwardToPartner] = useState(false);

    // Separate state for each document category
    const [purchaseFiles, setPurchaseFiles] = useState<File[]>([]);
    const [salesFiles, setSalesFiles] = useState<File[]>([]);

    const [activeCategory, setActiveCategory] = useState<null | 'purchase_invoice' | 'sales_invoice'>(null);
    const [isUploading, setIsUploading] = useState(false);

    const handleSetPurchaseFiles = (files: File[]) => {
        setPurchaseFiles(files);
        setActiveCategory(files.length > 0 ? 'purchase_invoice' : null);
    };

    const handleSetSalesFiles = (files: File[]) => {
        setSalesFiles(files);
        setActiveCategory(files.length > 0 ? 'sales_invoice' : null);
    };

    const handleCancelSelection = () => {
        setPurchaseFiles([]);
        setSalesFiles([]);
        setActiveCategory(null);
    };

    // Handler for uploading files with document_category
    async function onDocumentSelectionCompleteWithCategory(files_: File[], document_category: string) {
        if (files_.length === 0) return;
        const formData = new FormData();
        files_.forEach((file) => {
            formData.append('files', file, file.name);
        });
        formData.append('document_category', document_category);
        formData.append('shared_via', 'web');
        formData.append('business_id_override', business.id);
        formData.append('forward_to_partner', forwardToPartner.toString())

        try {
            const response = await api.postFormData('document-collection/share-files', formData);
            if (response.ok) {
                dispatch({
                    title: 'Documenten gedeeld',
                    description: 'De geselecteerde documenten zijn gedeeld',
                    type: 'success',
                });
                onLoad();
            } else {
                throw new Error('Failed to upload documents');
            }
        } catch (error) {
            dispatch({
                title: 'Fout opgetreden',
                description: 'Het development team is op de hoogte en zal contact opnemen',
                type: 'error',
            });
        }
    }

    async function onLoad() {
        setIsLoading(true);
        try {
            const result = await api.Admin__business__uploadedDocuments(business.id);
            setFileUploadHistory(result as unknown as SharedDocument[]);
        } catch (error) {
            console.error("Error loading documents:", error);
            dispatch({
                title: 'Fout bij laden documenten',
                description: 'Er is een fout opgetreden bij het laden van de documenten',
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        onLoad();
    }, [business.id]);

    return (
        <>
            <LoadingFrame isLoading={isLoading}>
                <h2 className="weight-600 text-2xl mb-6">Documenten Uploaden</h2>

                {purchaseFiles.length === 0 && salesFiles.length === 0 ? (
                    <div className="upload-container grid grid-cols-1 md:grid-cols-2 gap-6">
                        <DocumentDropzone
                            label="Inkoopfacturen / bon"
                            documentDirection="purchase_invoice"
                            supportedFileTypes={supportedFileTypes}
                            supportedFileExtensions={supportedFileExtensions}
                            maxFiles={maxFiles}
                            maxSize={maxSize}
                            onUpload={onDocumentSelectionCompleteWithCategory}
                            files={purchaseFiles}
                            setFiles={handleSetPurchaseFiles}
                        />
                        <DocumentDropzone
                            label="Verkoopfacturen"
                            documentDirection="sales_invoice"
                            supportedFileTypes={supportedFileTypes}
                            supportedFileExtensions={supportedFileExtensions}
                            maxFiles={maxFiles}
                            maxSize={maxSize}
                            onUpload={onDocumentSelectionCompleteWithCategory}
                            files={salesFiles}
                            setFiles={handleSetSalesFiles}
                        />
                    </div>
                ) : (
                    <div className="file-list rounded-lg my-4">
                        <LoadingFrame isLoading={isUploading}>
                            <div className="mb-4 font-semibold text-lg">
                                {activeCategory === 'purchase_invoice' ? 'Inkoopfacturen / bon' : 'Verkoopfacturen'}
                            </div>
                            <ul>
                                {(activeCategory === 'purchase_invoice' ? purchaseFiles : salesFiles).map((file) => (
                                    <li key={file.name}
                                        className="border border-gray-300 rounded-lg p-4 my-2 flex items-center justify-between cursor-pointer">
                                        <div><span>{file.name}</span> <span
                                            className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</span>
                                        </div>
                                        <div className="h-8 w-8 hover:bg-gray-100 rounded-full p-1">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6" onClick={() => {
                                                if (activeCategory === 'purchase_invoice') {
                                                    const newFiles = purchaseFiles.filter(f => f.name !== file.name);
                                                    setPurchaseFiles(newFiles);
                                                    if (newFiles.length === 0) setActiveCategory(null);
                                                } else if (activeCategory === 'sales_invoice') {
                                                    const newFiles = salesFiles.filter(f => f.name !== file.name);
                                                    setSalesFiles(newFiles);
                                                    if (newFiles.length === 0) setActiveCategory(null);
                                                }
                                            }}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </LoadingFrame>
                        <Field className="flex items-center justify-between max-w-lg mt-4 mb-4">
                            <span className="flex grow flex-col">
                                <Label as="span" passive className="text-sm/6 font-medium text-gray-900">
                                    Forward documents to partner
                                </Label>
                            </span>
                            <Switch
                                checked={forwardToPartner}
                                onChange={setForwardToPartner}
                                className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 data-[checked]:bg-primary"
                            >
                                <span
                                    aria-hidden="true"
                                    className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
                                />
                            </Switch>
                        </Field>
                        <div className="flex gap-3 mt-4">
                            <button
                                className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark"
                                onClick={async () => {
                                    if (activeCategory === 'purchase_invoice') {
                                        setIsUploading(true);
                                        await onDocumentSelectionCompleteWithCategory(purchaseFiles, 'purchase_invoice');
                                        setIsUploading(false);
                                        handleCancelSelection();
                                    } else if (activeCategory === 'sales_invoice') {
                                        setIsUploading(true);
                                        await onDocumentSelectionCompleteWithCategory(salesFiles, 'sales_invoice');
                                        setIsUploading(false);
                                        handleCancelSelection();
                                    }
                                }}
                                disabled={isUploading}
                            >
                                Uploaden
                            </button>
                            <button
                                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:opacity-80"
                                onClick={handleCancelSelection}
                                disabled={isUploading}
                            >
                                Annuleer
                            </button>
                        </div>
                    </div>
                )}

                <h2 className="weight-600 text-2xl mb-6 mt-16">Documenten</h2>
                <SharedDocumentsTable data={fileUploadHistory} />
            </LoadingFrame>
        </>
    );
}
